import type { CSSProperties } from 'react';

import { Viewport } from '@/theme/breakpoints';
import { zIndex } from '@/theme/zIndex';
import type {
  CpopoverAlign,
  PopoverEffectiveLayout,
  PopoverPosition,
} from './Cpopover.types';
import {
  CONTENT_MAX_HEIGHT_INHERIT,
  MOBILE_EDGE_MARGIN_PX,
  POPOVER_ALIGN,
} from './constants';

const ROOT_WIDTH_STYLE = '100%';
const POPOVER_POINTER_EVENTS = 'auto';

const TRANSFORM_ORIGIN_VERTICAL = {
  Bottom: 'bottom',
  Top: 'top',
} as const;

const TRANSFORM_ORIGIN_HORIZONTAL = {
  Center: 'center',
  Right: 'right',
  Left: 'left',
} as const;

const TRANSFORM_ORIGIN_HORIZONTAL_BY_ALIGN: Record<CpopoverAlign, string> = {
  [POPOVER_ALIGN.Center]: TRANSFORM_ORIGIN_HORIZONTAL.Center,
  [POPOVER_ALIGN.End]: TRANSFORM_ORIGIN_HORIZONTAL.Right,
  [POPOVER_ALIGN.Start]: TRANSFORM_ORIGIN_HORIZONTAL.Left,
};

export function getEffectivePopoverLayout(input: {
  align: CpopoverAlign;
  edgeMargin: number;
  viewport: Viewport | null;
}): PopoverEffectiveLayout {
  if (input.viewport === Viewport.Mobile) {
    return {
      align: POPOVER_ALIGN.Center,
      edgeMargin: MOBILE_EDGE_MARGIN_PX,
    };
  }

  return {
    align: input.align,
    edgeMargin: input.edgeMargin,
  };
}

export function getPopoverIsShown(input: {
  position: PopoverPosition | null;
  visible: boolean;
}): boolean {
  return input.visible && input.position != null;
}

export function getPopoverZIndex(input: { zIndexOverride?: number }): number {
  return input.zIndexOverride ?? zIndex.popover;
}

export function getPopoverPortalContainerId(input: {
  zIndexOverride?: number;
}): string | undefined {
  return input.zIndexOverride ? `portal-root-${input.zIndexOverride}` : undefined;
}

export function getPopoverTransformOrigin(input: {
  position: PopoverPosition;
}): string {
  const vertical = input.position.openAbove
    ? TRANSFORM_ORIGIN_VERTICAL.Bottom
    : TRANSFORM_ORIGIN_VERTICAL.Top;

  return `${vertical} ${TRANSFORM_ORIGIN_HORIZONTAL_BY_ALIGN[input.position.align]}`;
}

export function getPopoverMotionStyle(input: {
  popoverZIndex: number;
  position: PopoverPosition | null;
}): CSSProperties | null {
  if (!input.position) {
    return null;
  }

  const style: CSSProperties = {
    position: 'fixed',
    left: input.position.left,
    width: input.position.width,
    maxHeight: input.position.maxHeight,
    zIndex: input.popoverZIndex + 1,
    transformOrigin: getPopoverTransformOrigin({ position: input.position }),
    pointerEvents: POPOVER_POINTER_EVENTS,
    ...getPopoverVerticalPositionStyle({ position: input.position }),
  };

  return style;
}

function getPopoverVerticalPositionStyle(input: {
  position: PopoverPosition;
}): CSSProperties {
  if (input.position.openAbove && input.position.bottom != null) {
    return { bottom: input.position.bottom };
  }

  if (input.position.top != null) {
    return { top: input.position.top };
  }

  return {};
}

export function getPopoverSurfaceStyle(input: {
  surfaceStyle?: CSSProperties;
}): CSSProperties {
  return {
    width: ROOT_WIDTH_STYLE,
    ...input.surfaceStyle,
  };
}

export function getPopoverContentWrapperStyle(input: {
  contentWrapperStyle?: CSSProperties;
}): CSSProperties {
  return {
    width: ROOT_WIDTH_STYLE,
    maxHeight: CONTENT_MAX_HEIGHT_INHERIT,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    ...input.contentWrapperStyle,
  };
}
